<template>
  <ModalCardDefault
    :title="`Assinatura — ${companyName}`"
    @close="$emit('close')"
  >
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-else-if="subscription">
      <ExpansionPanel
        v-model="subscriptionPanel"
        title="Dados da Assinatura"
        :icon="$icons.accountTie"
        readonly
      >
        <Row>
          <Col cols="12" md="4">
            <strong>Plano:</strong> {{ subscription.plan?.name || '-' }}
          </Col>
          <Col cols="12" md="4">
            <strong>Data Inicial:</strong>
            {{ utils.formatDatePT(subscription.start_date) }}
          </Col>
          <Col cols="12" md="4">
            <strong>Dia Vencimento:</strong> {{ subscription.due_day || '-' }}
          </Col>
          <Col cols="12" md="4">
            <strong>Valor Final:</strong>
            {{ utils.formatCurrency(subscription.price_with_discount) }}
          </Col>
          <Col cols="12" md="4">
            <strong>Status:</strong>
            <Chip
              :label="getSubscriptionStatusLabel(subscription.status)"
              :color="getSubscriptionStatusColor(subscription.status)"
              class="ml-2"
            />
          </Col>
        </Row>
      </ExpansionPanel>

      <ExpansionPanel
        v-model="paymentsPanel"
        title="Pagamentos da Assinatura"
        :icon="$icons.box"
        readonly
        class="mt-4"
      >
        <DataTable
          no-search
          no-sync
          :items="payments"
          :headers="paymentHeaders"
          :slots="paymentSlots"
          :items-per-page="10"
        >
          <template v-slot:[`due_date`]="{ props: { item } }">
            {{ utils.formatDatePT(item.due_date) }}
          </template>

          <template v-slot:[`start_date`]="{ props: { item } }">
            {{ utils.formatDatePT(item.start_date) }}
          </template>

          <template v-slot:[`end_date`]="{ props: { item } }">
            {{ utils.formatDatePT(item.end_date) }}
          </template>

          <template v-slot:[`payment_date`]="{ props: { item } }">
            {{ item.payment_date ? utils.formatDatePT(item.payment_date) : '-' }}
          </template>

          <template v-slot:[`value`]="{ props: { item } }">
            {{ utils.formatCurrency(item.value) }}
          </template>

          <template v-slot:[`status`]="{ props: { item } }">
            <Chip
              :label="getPaymentStatusLabel(item.status)"
              :color="getPaymentStatusColor(item.status)"
            />
          </template>
        </DataTable>
      </ExpansionPanel>
    </template>

    <div v-else class="text-center py-6">
      Nenhuma assinatura encontrada para esta empresa.
    </div>
  </ModalCardDefault>
</template>

<script>
import ModalCardDefault from '@/components/ModalCardDefault';
import {
  ExpansionPanel,
  DataTable,
  Row,
  Col,
  Chip,
} from '@/components/vuetify';
import { formatDatePT, formatCurrency } from '@/utils';

const SUBSCRIPTION_STATUS = {
  active: { label: 'Ativo', color: 'success' },
  suspended: { label: 'Suspenso', color: 'warning' },
  pending: { label: 'Pendente', color: 'info' },
  waiting_payment: { label: 'Aguardando Pagamento', color: 'warning' },
  canceled: { label: 'Cancelado', color: 'error' },
  finished: { label: 'Finalizado', color: 'grey' },
};

const PAYMENT_STATUS = {
  paid: { label: 'Pago', color: 'success' },
  pending: { label: 'Pendente', color: 'warning' },
  overdue: { label: 'Vencido', color: 'error' },
};

export default {
  name: 'AdminCompanySubscriptionModal',
  components: {
    ModalCardDefault,
    ExpansionPanel,
    DataTable,
    Row,
    Col,
    Chip,
  },
  props: {
    companyId: {
      type: String,
      required: true,
    },
    companyName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      subscription: null,
      subscriptionPanel: [0],
      paymentsPanel: [0],
      utils: {
        formatDatePT,
        formatCurrency,
      },
      paymentSlots: [
        { field: 'due_date' },
        { field: 'start_date' },
        { field: 'end_date' },
        { field: 'payment_method' },
        { field: 'value' },
        { field: 'payment_date' },
        { field: 'status' },
      ],
    };
  },
  computed: {
    paymentHeaders() {
      return [
        { text: 'Vencimento', value: 'due_date' },
        { text: 'Início Vigência', value: 'start_date' },
        { text: 'Fim Vigência', value: 'end_date' },
        { text: 'Forma Pagamento', value: 'payment_method' },
        { text: 'Valor', value: 'value' },
        { text: 'Data Pag.', value: 'payment_date' },
        { text: 'Status', value: 'status' },
      ];
    },
    payments() {
      return (this.subscription?.payments || []).slice().sort(
        (a, b) => new Date(b.due_date) - new Date(a.due_date)
      );
    },
  },
  mounted() {
    this.fetchSubscription();
  },
  methods: {
    fetchSubscription() {
      this.loading = true;
      this.$api.admin
        .getCompanySubscription(this.companyId)
        .then(({ data }) => {
          this.subscription = data.subscription;
        })
        .catch(() => {
          this.$noty.error('Erro ao carregar a assinatura');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getSubscriptionStatusLabel(status) {
      return SUBSCRIPTION_STATUS[status]?.label || status || '-';
    },
    getSubscriptionStatusColor(status) {
      return SUBSCRIPTION_STATUS[status]?.color || 'grey';
    },
    getPaymentStatusLabel(status) {
      return PAYMENT_STATUS[status]?.label || status || '-';
    },
    getPaymentStatusColor(status) {
      return PAYMENT_STATUS[status]?.color || 'grey';
    },
  },
};
</script>
